import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { DataTableColDef } from '../DataTable.types';

const MIN_COLUMN_WIDTH = 60;
const DEFAULT_MIN_WIDTH = 80;
const DEFAULT_FLEX = 1;

const attachClickSuppressor = () => {
    const suppressClick = (ev: MouseEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
        window.removeEventListener('click', suppressClick, true);
    };
    window.addEventListener('click', suppressClick, true);
};

interface ClampHit {
    col: DataTableColDef;
    value: number;
}

/** Returns the first flex column whose pro-rata share falls outside its [min, max] clamp. */
const findClampedColumn = (
    cols: DataTableColDef[],
    remaining: number,
    totalFlex: number,
): ClampHit | null => {
    for (const col of cols) {
        const share = (remaining * (col.flex ?? DEFAULT_FLEX)) / totalFlex;
        const min = col.minWidth ?? DEFAULT_MIN_WIDTH;
        const max = col.maxWidth ?? Number.POSITIVE_INFINITY;
        if (share < min) return { col, value: min };
        if (share > max) return { col, value: max };
    }
    return null;
};

/** Iteratively assigns widths to flex columns, peeling off clamped ones until none remain. */
const distributeFlexColumns = (
    flexCols: DataTableColDef[],
    initialRemaining: number,
    out: Record<string, number>,
): void => {
    let cols = flexCols;
    let remaining = initialRemaining;

    while (cols.length > 0) {
        const totalFlex = cols.reduce((sum, c) => sum + (c.flex ?? DEFAULT_FLEX), 0);
        if (totalFlex <= 0) return;

        const clamped = findClampedColumn(cols, remaining, totalFlex);
        if (!clamped) {
            // No further clamps needed — split the rest by weight exactly.
            for (const col of cols) {
                out[col.field] = (remaining * (col.flex ?? DEFAULT_FLEX)) / totalFlex;
            }
            return;
        }

        out[clamped.col.field] = clamped.value;
        remaining -= clamped.value;
        cols = cols.filter((c) => c !== clamped.col);

        if (remaining < 0) {
            // Out of space — clamp all remaining to their min.
            for (const col of cols) {
                out[col.field] = col.minWidth ?? DEFAULT_MIN_WIDTH;
            }
            return;
        }
    }
};

/**
 * Compute pixel widths for each column given the container width and any user
 * overrides from drag-resizing. Fixed/overridden columns keep their pixel value;
 * flex columns share leftover space by weight, clamped to [minWidth, maxWidth].
 * Iterates until no column is clamped.
 */
export const distributeColumnWidths = (
    columns: DataTableColDef[],
    containerWidth: number,
    overrides: Record<string, number>,
): Record<string, number> => {
    const out: Record<string, number> = {};
    const activeFlexCols: DataTableColDef[] = [];
    let usedPixels = 0;

    for (const col of columns) {
        const fixed = overrides[col.field] ?? col.width;
        if (typeof fixed === 'number') {
            out[col.field] = fixed;
            usedPixels += fixed;
        } else {
            activeFlexCols.push(col);
        }
    }

    distributeFlexColumns(activeFlexCols, Math.max(0, containerWidth - usedPixels), out);
    return out;
};

export interface UseColumnResize {
    containerRef: React.RefObject<HTMLDivElement | null>;
    activeResizeField: string | null;
    getColumnWidth: (column: DataTableColDef) => number;
    onResizeStart: (field: string, e: React.MouseEvent) => void;
    onResizeHover: (field: string | null) => void;
}

export const useColumnResize = (columns: DataTableColDef[], reservedWidth = 0): UseColumnResize => {
    // Drag-resized pixel overrides; win over a column's `flex` weight.
    const [userOverrides, setUserOverrides] = useState<Record<string, number>>({});
    const [containerWidth, setContainerWidth] = useState(0);
    const [activeResizeField, setActiveResizeField] = useState<string | null>(null);
    const isResizingRef = useRef(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const resizeHandlersRef = useRef<{
        move: (ev: MouseEvent) => void;
        up: () => void;
    } | null>(null);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const update = () => setContainerWidth(el.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const widths = useMemo(
        () => distributeColumnWidths(columns, containerWidth - reservedWidth, userOverrides),
        [columns, containerWidth, reservedWidth, userOverrides],
    );

    const widthsRef = useRef(widths);
    widthsRef.current = widths;

    const cleanupResize = useCallback(() => {
        if (resizeHandlersRef.current) {
            window.removeEventListener('mousemove', resizeHandlersRef.current.move);
            window.removeEventListener('mouseup', resizeHandlersRef.current.up);
            resizeHandlersRef.current = null;
        }
    }, []);

    const onResizeHover = useCallback((field: string | null) => {
        if (isResizingRef.current) return;
        setActiveResizeField(field);
    }, []);

    const onResizeStart = useCallback(
        (field: string, e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            const startX = e.clientX;
            const startWidth = widthsRef.current[field] ?? MIN_COLUMN_WIDTH;
            let moved = false;
            isResizingRef.current = true;
            setActiveResizeField(field);

            const handleMove = (ev: MouseEvent) => {
                moved = true;
                const delta = ev.clientX - startX;
                const next = Math.max(MIN_COLUMN_WIDTH, startWidth + delta);
                setUserOverrides((prev) =>
                    prev[field] === next ? prev : { ...prev, [field]: next },
                );
            };
            const handleUp = () => {
                cleanupResize();
                isResizingRef.current = false;
                setActiveResizeField(null);
                if (moved) attachClickSuppressor();
            };
            cleanupResize();
            resizeHandlersRef.current = { move: handleMove, up: handleUp };
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', handleUp);
        },
        [cleanupResize],
    );

    useEffect(() => cleanupResize, [cleanupResize]);

    const getColumnWidth = useCallback(
        (column: DataTableColDef): number =>
            widths[column.field] ?? column.width ?? column.minWidth ?? DEFAULT_MIN_WIDTH,
        [widths],
    );

    return {
        containerRef,
        activeResizeField,
        getColumnWidth,
        onResizeStart,
        onResizeHover,
    };
};
